import React, { useState, useEffect } from "react";
import {
  TrendingUp,
  Users,
  Award,
  Target,
  Calendar,
  CheckCircle,
  BarChart3,
  PieChart,
  Activity,
  ArrowRight,
  Heart,
  Shield,
  BookOpen,
  Globe,
} from "lucide-react";

const Impact = () => {
  const [scrollY, setScrollY] = useState(0);
  const [activeMetric, setActiveMetric] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const impactMetrics = [
    {
    category: "Infectious Disease Control",
    icon: Shield,
    color: "primary",
    stats: [
      {
        number: "12%",
        label: "HIV Prevalence in Rakai",
        description: "Above 5.4% national average - urgent intervention needed",
      },
      {
        number: "3-Year",
        label: "Implementation Timeline",
        description: "Phased approach to integrated disease control",
      },
      {
        number: "Zero",
        label: "New Infections Goal",
        description: "Target for HIV and TB by 2030",
      },
      {
        number: "Mobile",
        label: "Outreach Clinics",
        description: "Bringing services to high-burden communities",
      },
    ],
  },
    {
      category: "Direct Beneficiaries",
      icon: Users,
      color: "primary",
      stats: [
        {
          number: "200",
          label: "Adolescent Girls & Young Women (10-24)",
          description: "Directly reached through our programs",
        },
        {
          number: "30",
          label: "Healthcare Providers Trained",
          description: "Equipped with SGBV response skills",
        },
        {
          number: "25",
          label: "Educators & Community Leaders",
          description: "Trained in prevention strategies",
        },
        {
          number: "80+",
          label: "Family Members Engaged",
          description: "Indirect beneficiaries of our interventions",
        },
      ],
    },
    {
      category: "Program Outcomes",
      icon: Target,
      color: "success",
      stats: [
        {
          number: "85%",
          label: "Increased Awareness",
          description: "Community members report improved SGBV awareness",
        },
        {
          number: "70%",
          label: "Improved Help-Seeking",
          description: "Survivors accessing support services",
        },
        {
          number: "90%",
          label: "Skill Development",
          description: "AGYW demonstrating improved life skills",
        },
        {
          number: "80%",
          label: "Knowledge Retention",
          description: "Trained providers maintaining competency",
        },
      ],
    },
    {
      category: "System Strengthening",
      icon: Shield,
      color: "warning",
      stats: [
        {
          number: "5",
          label: "Safe Spaces Established",
          description: "Functional support centers for survivors",
        },
        {
          number: "12",
          label: "Referral Pathways",
          description: "Active coordination mechanisms",
        },
        {
          number: "25",
          label: "Community Dialogues",
          description: "Facilitating social norm change",
        },
        {
          number: "100%",
          label: "Stakeholder Engagement",
          description: "Multi-sectoral collaboration achieved",
        },
      ],
    },
    {
      category: "Sustainability Indicators",
      icon: TrendingUp,
      color: "info",
      stats: [
        {
          number: "15",
          label: "Community Committees",
          description: "Established for ongoing oversight",
        },
        {
          number: "8",
          label: "Policy Influences",
          description: "Local policies strengthened",
        },
        {
          number: "95%",
          label: "Partner Satisfaction",
          description: "Stakeholders rating program positively",
        },
        {
          number: "18",
          label: "Months Implementation",
          description: "Successful program completion",
        },
      ],
    },
  ];

  const expectedOutcomes = [
    {
      title: "Enhanced Community Awareness",
      description:
        "Increased community awareness and proactive engagement in SGBV prevention through comprehensive education campaigns.",
      icon: BookOpen,
      progress: 85,
      achievements: [
        "Community dialogues reaching 1,500+ participants",
        "Awareness campaigns in 10 sub-counties",
        "Male engagement initiatives with 300+ participants",
        "Social media campaigns reaching 5,000+ people",
      ],
    },
    {
      title: "Improved Access to Support Services",
      description:
        "Enhanced access to quality support services for AGYW survivors through strengthened systems and pathways.",
      icon: Heart,
      progress: 78,
      achievements: [
        "5 safe spaces providing comprehensive care",
        "24/7 helpline with trained counselors",
        "Legal aid services for 150+ survivors",
        "Health services including PEP and contraceptives",
      ],
    },
    {
      title: "Empowered AGYW Population",
      description:
        "Empowered AGYW with improved self-efficacy and decision-making capabilities through targeted interventions.",
      icon: Users,
      progress: 92,
      achievements: [
        "Life skills training for 800+ AGYW",
        "Vocational training programs for 200+ participants",
        "Mentorship matching 150 AGYW with role models",
        "Leadership development for 100+ young women",
      ],
    },
    {
      title: "Strengthened Coordination Systems",
      description:
        "Strengthened coordination among stakeholders leading to a cohesive and effective SGBV response system.",
      icon: Globe,
      progress: 88,
      achievements: [
        "Multi-stakeholder coordination platform established",
        "Quarterly coordination meetings with 20+ partners",
        "Resource sharing agreements with 8 organizations",
        "Joint programming initiatives launched",
      ],
    },
  ];

  const timelineData = [
    {
      phase: "Foundation Phase",
      period: "Months 1-6",
      achievements: [
        "Baseline study completed with 500 respondents",
        "Community entry and stakeholder mapping",
        "Staff recruitment and capacity building",
        "Partnership agreements signed with 15 organizations",
      ],
      impact: "Strong foundation established for program implementation",
    },
    {
      phase: "Implementation Phase",
      period: "Months 7-12",
      achievements: [
        "Prevention campaigns launched in 8 communities",
        "Safe spaces established and operationalized",
        "Training programs delivered to 130 service providers",
        "Empowerment initiatives reached 1,200 AGYW",
      ],
      impact: "Core program activities delivering measurable results",
    },
    {
      phase: "Consolidation Phase",
      period: "Months 13-18",
      achievements: [
        "Program evaluation conducted with external support",
        "Sustainability plans developed with communities",
        "Knowledge products created and disseminated",
        "Transition strategies implemented successfully",
      ],
      impact: "Sustainable systems established for continued impact",
    },
  ];

  const researchFindings = [
    {
      finding: "Reduced Tolerance for SGBV",
      baseline: "45%",
      current: "78%",
      change: "+33%",
      description:
        "Community members expressing zero tolerance for SGBV practices",
    },
    {
      finding: "Increased Help-Seeking Behavior",
      baseline: "25%",
      current: "65%",
      change: "+40%",
      description: "Survivors reporting incidents and seeking support services",
    },
    {
      finding: "Improved Male Engagement",
      baseline: "15%",
      current: "58%",
      change: "+43%",
      description: "Men actively participating in SGBV prevention activities",
    },
    {
      finding: "Enhanced Service Quality",
      baseline: "40%",
      current: "85%",
      change: "+45%",
      description: "Service providers demonstrating competent SGBV response",
    },
  ];

  return (
    <div className="impact-page">
      {/* Bootstrap CSS */}
      <link
        href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css"
        rel="stylesheet"
      />

      {/* Animated Background Elements */}
      <div className="impact-background">
        <div className="bg-element bg-element-1"></div>
        <div className="bg-element bg-element-2"></div>
        <div className="bg-element bg-element-3"></div>
        <div className="bg-element bg-element-4"></div>
        <div className="metric-circle metric-circle-1"></div>
        <div className="metric-circle metric-circle-2"></div>
        <div className="metric-circle metric-circle-3"></div>
      </div>

      {/* Hero Section */}
      <section className="impact-hero">
        <div className="container">
          <div className="row align-items-center min-vh-60">
            <div className="col-lg-8">
              <div className="hero-content">
                <span className="hero-badge">Our Impact</span>
                <h1 className="hero-title">
                  Creating Measurable Change in Rakai District
                </h1>
                <p className="hero-subtitle">
                  Through evidence-based interventions and community-centered
                  approaches, we're making a tangible difference in the fight
                  against Sexual and Gender-Based Violence, empowering women and
                  girls to live safer, more fulfilling lives.
                </p>
              </div>
            </div>
            <div className="col-lg-4">
              <div className="impact-visual">
                <div className="impact-shape-container">
                  <div className="impact-shape shape-chart">
                    <div className="chart-bars">
                      <div className="bar bar-1"></div>
                      <div className="bar bar-2"></div>
                      <div className="bar bar-3"></div>
                      <div className="bar bar-4"></div>
                    </div>
                  </div>
                  <div className="impact-shape shape-growth"></div>
                  <div className="impact-shape shape-target"></div>
                  <div className="impact-metrics">
                    <div className="metric metric-1">
                      <div className="metric-icon">📊</div>
                      <div className="metric-value">85%</div>
                    </div>
                    <div className="metric metric-2">
                      <div className="metric-icon">👥</div>
                      <div className="metric-value">2.7K</div>
                    </div>
                    <div className="metric metric-3">
                      <div className="metric-icon">📈</div>
                      <div className="metric-value">+40%</div>
                    </div>
                  </div>
                  <div className="impact-particles">
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                    <div className="particle"></div>
                  </div>
                  <div className="impact-waves">
                    <div className="wave wave-1"></div>
                    <div className="wave wave-2"></div>
                    <div className="wave wave-3"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style jsx>{`
        /* Impact Hero Section Enhanced Styles */
        .impact-hero {
          padding: 2rem 0;
          background: linear-gradient(
            135deg,
            var(--light-gray) 0%,
            #ffffff 100%
          );
          position: relative;
          overflow: hidden;
        }

        .impact-visual {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          min-height: 450px;
        }

        .impact-shape-container {
          position: relative;
          width: 320px;
          height: 320px;
        }

        /* Main Impact Shapes */
        .impact-shape {
          position: absolute;
          border-radius: 20px;
          animation: impactFloat 8s ease-in-out infinite;
        }

        .impact-shape.shape-chart {
          width: 160px;
          height: 120px;
          background: linear-gradient(
            135deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          top: 40px;
          left: 30px;
          display: flex;
          align-items: flex-end;
          justify-content: center;
          padding: 20px 15px;
          box-shadow: 0 10px 30px rgba(37, 99, 235, 0.3);
          animation-delay: 0s;
        }

        .chart-bars {
          display: flex;
          align-items: flex-end;
          gap: 8px;
          height: 60px;
        }

        .bar {
          width: 12px;
          background: rgba(255, 255, 255, 0.9);
          border-radius: 6px;
          animation: barGrow 2s ease-out infinite;
        }

        .bar.bar-1 {
          height: 30px;
          animation-delay: 0s;
        }
        .bar.bar-2 {
          height: 50px;
          animation-delay: 0.2s;
        }
        .bar.bar-3 {
          height: 40px;
          animation-delay: 0.4s;
        }
        .bar.bar-4 {
          height: 60px;
          animation-delay: 0.6s;
        }

        .impact-shape.shape-growth {
          width: 100px;
          height: 100px;
          background: linear-gradient(45deg, var(--success-green), #34d399);
          border-radius: 50%;
          top: 50px;
          right: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
          animation-delay: 2s;
        }

        .impact-shape.shape-growth::after {
          content: "↗";
          font-size: 2rem;
          color: white;
          font-weight: bold;
        }

        .impact-shape.shape-target {
          width: 80px;
          height: 80px;
          background: linear-gradient(225deg, var(--accent-orange), #fbbf24);
          border-radius: 50%;
          bottom: 60px;
          left: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 6px 20px rgba(245, 158, 11, 0.3);
          animation-delay: 4s;
        }

        .impact-shape.shape-target::after {
          content: "🎯";
          font-size: 1.5rem;
        }

        /* Floating Metrics */
        .impact-metrics {
          position: absolute;
          inset: 0;
        }

        .metric {
          position: absolute;
          background: white;
          border-radius: 12px;
          padding: 8px 12px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 0.8rem;
          font-weight: 600;
          animation: metricFloat 6s ease-in-out infinite;
        }

        .metric.metric-1 {
          top: 20px;
          right: 20px;
          color: var(--primary-blue);
          animation-delay: 1s;
        }

        .metric.metric-2 {
          bottom: 100px;
          right: 10px;
          color: var(--success-green);
          animation-delay: 3s;
        }

        .metric.metric-3 {
          bottom: 30px;
          left: 10px;
          color: var(--accent-orange);
          animation-delay: 5s;
        }

        .metric-icon {
          font-size: 1rem;
        }

        .metric-value {
          font-weight: 700;
        }

        /* Decorative Particles */
        .impact-particles {
          position: absolute;
          inset: 0;
        }

        .particle {
          position: absolute;
          width: 6px;
          height: 6px;
          background: var(--accent-teal);
          border-radius: 50%;
          opacity: 0.6;
          animation: particleFloat 4s ease-in-out infinite;
        }

        .particle:nth-child(1) {
          top: 15%;
          left: 20%;
          animation-delay: 0s;
        }
        .particle:nth-child(2) {
          top: 25%;
          right: 15%;
          animation-delay: 1s;
        }
        .particle:nth-child(3) {
          bottom: 30%;
          left: 15%;
          animation-delay: 2s;
        }
        .particle:nth-child(4) {
          bottom: 20%;
          right: 25%;
          animation-delay: 3s;
        }
        .particle:nth-child(5) {
          top: 60%;
          left: 70%;
          animation-delay: 2.5s;
        }
        .particle:nth-child(6) {
          top: 80%;
          left: 40%;
          animation-delay: 1.5s;
        }

        /* Background Waves */
        .impact-waves {
          position: absolute;
          inset: 0;
          z-index: -1;
        }

        .wave {
          position: absolute;
          border: 2px solid;
          border-radius: 50%;
          animation: waveExpand 5s ease-out infinite;
        }

        .wave.wave-1 {
          width: 200px;
          height: 200px;
          border-color: rgba(37, 99, 235, 0.1);
          top: 60px;
          left: 60px;
          animation-delay: 0s;
        }

        .wave.wave-2 {
          width: 150px;
          height: 150px;
          border-color: rgba(16, 185, 129, 0.08);
          top: 85px;
          left: 85px;
          animation-delay: 1.5s;
        }

        .wave.wave-3 {
          width: 250px;
          height: 250px;
          border-color: rgba(245, 158, 11, 0.06);
          top: 35px;
          left: 35px;
          animation-delay: 3s;
        }

        /* Animations */
        @keyframes impactFloat {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-15px) rotate(2deg);
          }
        }

        @keyframes barGrow {
          0%,
          70% {
            transform: scaleY(0.7);
          }
          85% {
            transform: scaleY(1.1);
          }
          100% {
            transform: scaleY(1);
          }
        }

        @keyframes metricFloat {
          0%,
          100% {
            transform: translateY(0px) scale(1);
          }
          50% {
            transform: translateY(-10px) scale(1.05);
          }
        }

        @keyframes particleFloat {
          0%,
          100% {
            transform: translateY(0px);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-20px);
            opacity: 1;
          }
        }

        @keyframes waveExpand {
          0% {
            transform: scale(0.8);
            opacity: 0.3;
          }
          50% {
            opacity: 0.1;
          }
          100% {
            transform: scale(1.2);
            opacity: 0;
          }
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .impact-visual {
            margin-top: 3rem;
            min-height: 350px;
          }

          .impact-shape-container {
            width: 280px;
            height: 280px;
          }

          .impact-shape.shape-chart {
            width: 140px;
            height: 100px;
          }

          .impact-shape.shape-growth {
            width: 80px;
            height: 80px;
          }

          .impact-shape.shape-target {
            width: 70px;
            height: 70px;
          }

          .chart-bars {
            height: 50px;
          }

          .bar {
            width: 10px;
          }
        }

        @media (max-width: 768px) {
          .impact-visual {
            min-height: 300px;
          }

          .impact-shape-container {
            width: 240px;
            height: 240px;
          }

          .impact-shape.shape-chart {
            width: 120px;
            height: 80px;
            padding: 15px 10px;
          }

          .impact-shape.shape-growth {
            width: 70px;
            height: 70px;
          }

          .impact-shape.shape-target {
            width: 60px;
            height: 60px;
          }

          .chart-bars {
            height: 40px;
            gap: 6px;
          }

          .bar {
            width: 8px;
          }

          .metric {
            padding: 6px 10px;
            font-size: 0.7rem;
          }

          .particle {
            width: 4px;
            height: 4px;
          }
        }

        /* Accessibility */
        @media (prefers-reduced-motion: reduce) {
          .impact-shape,
          .metric,
          .particle,
          .wave,
          .bar {
            animation: none;
          }
        }

        /* Enhanced Visual Effects */
        .impact-shape.shape-chart::before {
          content: "";
          position: absolute;
          top: -5px;
          left: -5px;
          right: -5px;
          bottom: -5px;
          background: linear-gradient(
            135deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          border-radius: 25px;
          opacity: 0.3;
          z-index: -1;
          animation: pulseGlow 3s ease-in-out infinite;
        }

        @keyframes pulseGlow {
          0%,
          100% {
            opacity: 0.3;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.05);
          }
        }
      `}</style>

      {/* Impact Metrics Dashboard */}
      <section className="metrics-dashboard">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Impact Metrics</span>
            <h2 className="section-title">Measuring Our Success</h2>
            <p className="section-description">
              Comprehensive data showing the tangible impact of our
              interventions across prevention, protection, empowerment, and
              coordination pillars.
            </p>
          </div>

          {/* Metric Categories Tabs */}
          <div className="metrics-tabs">
            <div className="tab-navigation">
              {impactMetrics.map((category, index) => (
                <button
                  key={index}
                  className={`metric-tab ${
                    activeMetric === index ? "active" : ""
                  }`}
                  onClick={() => setActiveMetric(index)}
                >
                  <category.icon size={20} />
                  <span>{category.category}</span>
                </button>
              ))}
            </div>

            <div className="tab-content">
              {impactMetrics.map((category, categoryIndex) => (
                <div
                  key={categoryIndex}
                  className={`metric-panel ${
                    activeMetric === categoryIndex ? "active" : ""
                  }`}
                >
                  <div className="row g-4">
                    {category.stats.map((stat, statIndex) => (
                      <div key={statIndex} className="col-lg-3 col-md-6">
                        <div
                          className="metric-card"
                          style={{
                            transform: `translateY(${
                              Math.sin((scrollY + statIndex * 150) * 0.004) * 8
                            }px)`,
                            animationDelay: `${statIndex * 0.1}s`,
                          }}
                        >
                          <div
                            className={`metric-number text-${category.color}`}
                          >
                            {stat.number}
                          </div>
                          <h4 className="metric-title">{stat.label}</h4>
                          <p className="metric-description">
                            {stat.description}
                          </p>
                          <div
                            className={`metric-indicator bg-${category.color}`}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Expected Outcomes Progress */}
      <section className="outcomes-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Program Outcomes</span>
            <h2 className="section-title">Achieving Our Objectives</h2>
            <p className="section-description">
              Progress toward our four strategic objectives, demonstrating
              tangible results across prevention, protection, empowerment, and
              coordination.
            </p>
          </div>

          <div className="row g-4">
            {expectedOutcomes.map((outcome, index) => (
              <div key={index} className="col-lg-6">
                <div
                  className="outcome-card"
                  style={{
                    transform: `translateY(${
                      Math.sin((scrollY + index * 250) * 0.003) * 12
                    }px)`,
                    animationDelay: `${index * 0.15}s`,
                  }}
                >
                  <div className="outcome-header">
                    <div className="outcome-icon">
                      <outcome.icon size={28} />
                    </div>
                    <div className="outcome-progress">
                      <div className="progress-circle">
                        <svg width="60" height="60" viewBox="0 0 60 60">
                          <circle
                            cx="30"
                            cy="30"
                            r="25"
                            fill="none"
                            stroke="rgba(37, 99, 235, 0.1)"
                            strokeWidth="4"
                          />
                          <circle
                            cx="30"
                            cy="30"
                            r="25"
                            fill="none"
                            stroke="var(--primary-blue)"
                            strokeWidth="4"
                            strokeDasharray={`${outcome.progress * 1.57} 157`}
                            strokeLinecap="round"
                            transform="rotate(-90 30 30)"
                          />
                        </svg>
                        <span className="progress-text">
                          {outcome.progress}%
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="outcome-content">
                    <h3 className="outcome-title">{outcome.title}</h3>
                    <p className="outcome-description">{outcome.description}</p>

                    <div className="outcome-achievements">
                      <h6 className="achievements-title">Key Achievements</h6>
                      <ul className="achievements-list">
                        {outcome.achievements.map((achievement, idx) => (
                          <li key={idx}>
                            <CheckCircle size={16} />
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Timeline */}
      <section className="timeline-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">Implementation Journey</span>
            <h2 className="section-title">18-Month Impact Timeline</h2>
            <p className="section-description">
              A chronological view of our program implementation, showing key
              milestones and achievements across the three main phases of our
              intervention.
            </p>
          </div>

          <div className="timeline-container">
            {timelineData.map((phase, index) => (
              <div key={index} className="timeline-phase">
                <div className="phase-marker">
                  <span className="phase-number">{index + 1}</span>
                </div>
                <div className="phase-content">
                  <div className="phase-header">
                    <h3 className="phase-title">{phase.phase}</h3>
                    <span className="phase-period">{phase.period}</span>
                  </div>
                  <div className="phase-achievements">
                    <ul>
                      {phase.achievements.map((achievement, idx) => (
                        <li key={idx}>
                          <CheckCircle size={16} />
                          {achievement}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="phase-impact">
                    <strong>Impact: </strong>
                    <span>{phase.impact}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        :root {
          --primary-blue: #2563eb;
          --secondary-blue: #1e40af;
          --accent-teal: #06b6d4;
          --accent-orange: #f59e0b;
          --success-green: #10b981;
          --text-gray: #6b7280;
          --light-gray: #f8fafc;
          --info-blue: #3b82f6;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #374151;
          padding-top: 80px;
          overflow-x: hidden;
        }

        /* Animated Background Elements */
        .impact-background {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          pointer-events: none;
          z-index: -1;
        }

        .bg-element {
          position: absolute;
          background: linear-gradient(
            45deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          opacity: 0.03;
          border-radius: 20px;
          animation: floatRotate 25s ease-in-out infinite;
        }

        .bg-element-1 {
          width: 300px;
          height: 200px;
          top: 15%;
          right: 10%;
          animation-delay: 0s;
        }

        .bg-element-2 {
          width: 250px;
          height: 150px;
          bottom: 25%;
          left: 8%;
          animation-delay: 8s;
        }

        .bg-element-3 {
          width: 200px;
          height: 300px;
          top: 50%;
          right: 15%;
          animation-delay: 15s;
        }

        .bg-element-4 {
          width: 150px;
          height: 100px;
          bottom: 10%;
          right: 25%;
          animation-delay: 20s;
        }

        .metric-circle {
          position: absolute;
          border: 2px solid var(--accent-orange);
          border-radius: 50%;
          opacity: 0.1;
          animation: pulse 4s ease-in-out infinite;
        }

        .metric-circle-1 {
          width: 100px;
          height: 100px;
          top: 20%;
          left: 15%;
          animation-delay: 0s;
        }

        .metric-circle-2 {
          width: 80px;
          height: 80px;
          bottom: 30%;
          right: 20%;
          animation-delay: 2s;
        }

        .metric-circle-3 {
          width: 60px;
          height: 60px;
          top: 70%;
          left: 70%;
          animation-delay: 4s;
        }

        @keyframes floatRotate {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          25% {
            transform: translateY(-30px) rotate(5deg);
          }
          50% {
            transform: translateY(-60px) rotate(0deg);
          }
          75% {
            transform: translateY(-30px) rotate(-5deg);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.1;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.05;
          }
        }

        /* Hero Section */
        .impact-hero {
          padding: 2rem 0;
          background: linear-gradient(
            135deg,
            var(--light-gray) 0%,
            #ffffff 100%
          );
          position: relative;
        }

        .min-vh-60 {
          min-height: 60vh;
        }

        .hero-content {
          max-width: 700px;
        }

        .hero-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1.5rem;
          display: inline-block;
        }

        .hero-title {
          font-size: clamp(2.5rem, 5vw, 4rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.1;
        }

        .hero-subtitle {
          font-size: 1.2rem;
          color: var(--text-gray);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .hero-highlights {
          display: flex;
          gap: 2rem;
          flex-wrap: wrap;
        }

        .highlight-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          background: white;
          padding: 1.5rem;
          border-radius: 1rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
        }

        .highlight-icon {
          background: var(--primary-blue);
          color: white;
          padding: 0.75rem;
          border-radius: 50%;
        }

        .highlight-content {
          display: flex;
          flex-direction: column;
        }

        .highlight-number {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--primary-blue);
          line-height: 1;
        }

        .highlight-label {
          font-size: 0.9rem;
          color: var(--text-gray);
          font-weight: 500;
        }

        /* Section Headers */
        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-badge {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1.5rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 1rem;
          display: inline-block;
        }

        .section-title {
          font-size: clamp(2.5rem, 4vw, 3.5rem);
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .section-description {
          font-size: 1.2rem;
          color: var(--text-gray);
          max-width: 700px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* Metrics Dashboard */
        .metrics-dashboard {
          padding: 6rem 0;
        }

        .metrics-tabs {
          background: white;
          border-radius: 1.5rem;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .tab-navigation {
          display: flex;
          background: var(--light-gray);
          padding: 1rem;
          gap: 0.5rem;
          overflow-x: auto;
        }

        .metric-tab {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 1rem 1.5rem;
          background: transparent;
          border: none;
          border-radius: 0.75rem;
          color: var(--text-gray);
          font-weight: 500;
          transition: all 0.3s ease;
          white-space: nowrap;
        }

        .metric-tab.active {
          background: var(--primary-blue);
          color: white;
        }

        .metric-tab:hover {
          background: rgba(37, 99, 235, 0.1);
        }

        .metric-tab.active:hover {
          background: var(--secondary-blue);
        }

        .tab-content {
          padding: 3rem;
        }

        .metric-panel {
          display: none;
        }

        .metric-panel.active {
          display: block;
          animation: fadeIn 0.5s ease-out;
        }

        .metric-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2rem;
          text-align: center;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          position: relative;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .metric-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
        }

        .metric-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(
            90deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          border-radius: 1.5rem 1.5rem 0 0;
        }

        .metric-number {
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          line-height: 1;
        }

        .metric-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .metric-description {
          color: var(--text-gray);
          font-size: 0.95rem;
          line-height: 1.5;
          margin-bottom: 1rem;
        }

        .metric-indicator {
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 50px;
          height: 4px;
          border-radius: 2px;
        }

        /* Outcomes Section */
        .outcomes-section {
          padding: 6rem 0;
          background: var(--light-gray);
        }

        .outcome-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
          height: 100%;
          animation: fadeInUp 0.6s ease-out forwards;
          opacity: 0;
        }

        .outcome-card:hover {
          transform: translateY(-10px) !important;
          box-shadow: 0 25px 80px rgba(0, 0, 0, 0.15);
        }

        .outcome-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
        }

        .outcome-icon {
          background: var(--primary-blue);
          color: white;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .outcome-progress {
          position: relative;
        }

        .progress-circle {
          position: relative;
          width: 60px;
          height: 60px;
        }

        .progress-text {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--primary-blue);
        }

        .outcome-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .outcome-description {
          color: var(--text-gray);
          margin-bottom: 2rem;
          line-height: 1.6;
        }

        .outcome-achievements {
          border-top: 1px solid rgba(0, 0, 0, 0.05);
          padding-top: 1.5rem;
        }

        .achievements-title {
          font-size: 0.9rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin-bottom: 1rem;
        }

        .achievements-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .achievements-list li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 0.75rem;
          color: var(--text-gray);
          font-size: 0.9rem;
          line-height: 1.5;
        }

        .achievements-list li svg {
          color: var(--success-green);
          margin-top: 0.1rem;
          flex-shrink: 0;
        }

        /* Timeline Section */
        .timeline-section {
        }

        .timeline-container {
          max-width: 900px;
          margin: 0 auto;
          position: relative;
        }

        .timeline-container::before {
          content: "";
          position: absolute;
          left: 30px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(
            to bottom,
            var(--primary-blue),
            var(--accent-teal),
            var(--accent-orange)
          );
        }

        .timeline-phase {
          position: relative;
          margin-bottom: 4rem;
          padding-left: 100px;
        }

        .phase-marker {
          position: absolute;
          left: 0;
          top: 0;
          width: 60px;
          height: 60px;
          background: var(--primary-blue);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 700;
          font-size: 1.2rem;
          box-shadow: 0 4px 20px rgba(37, 99, 235, 0.3);
          z-index: 2;
        }

        .phase-content {
          background: white;
          border-radius: 1.5rem;
          padding: 2.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          position: relative;
        }

        .phase-content::before {
          content: "";
          position: absolute;
          left: -15px;
          top: 30px;
          width: 0;
          height: 0;
          border-top: 10px solid transparent;
          border-bottom: 10px solid transparent;
          border-right: 15px solid white;
        }

        .phase-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }

        .phase-title {
          font-size: 1.8rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin: 0;
        }

        .phase-period {
          background: rgba(37, 99, 235, 0.1);
          color: var(--primary-blue);
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .phase-achievements ul {
          list-style: none;
          padding: 0;
          margin: 0 0 1.5rem 0;
        }

        .phase-achievements li {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          margin-bottom: 1rem;
          color: var(--text-gray);
          line-height: 1.5;
        }

        .phase-achievements li svg {
          color: var(--success-green);
          margin-top: 0.2rem;
          flex-shrink: 0;
        }

        .phase-impact {
          background: var(--light-gray);
          padding: 1.5rem;
          border-radius: 1rem;
          color: var(--text-gray);
          font-style: italic;
          border-left: 4px solid var(--accent-orange);
        }

        /* Research Section */
        .research-section {
          background: var(--light-gray);
        }

        .research-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .research-card {
          background: white;
          border-radius: 1.5rem;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .research-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
        }

        .research-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 1.5rem;
          gap: 1rem;
        }

        .research-title {
          font-size: 1.2rem;
          font-weight: 700;
          color: var(--secondary-blue);
          margin: 0;
          flex: 1;
        }

        .change-indicator {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 600;
        }

        .change-indicator.positive {
          background: rgba(16, 185, 129, 0.1);
          color: var(--success-green);
        }

        .research-metrics {
          margin-bottom: 1.5rem;
        }

        .metric-comparison {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
        }

        .baseline-metric,
        .current-metric {
          text-align: center;
          flex: 1;
        }

        .metric-label {
          display: block;
          font-size: 0.8rem;
          color: var(--text-gray);
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 0.5rem;
        }

        .metric-value {
          display: block;
          font-size: 2rem;
          font-weight: 700;
          color: var(--text-gray);
        }

        .metric-value.current {
          color: var(--success-green);
        }

        .comparison-arrow {
          color: var(--primary-blue);
          flex-shrink: 0;
        }

        .research-description {
          color: var(--text-gray);
          margin-bottom: 1.5rem;
          line-height: 1.5;
          font-size: 0.95rem;
        }

        .progress-bar {
          width: 100%;
          height: 8px;
          background: rgba(37, 99, 235, 0.1);
          border-radius: 4px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(
            90deg,
            var(--primary-blue),
            var(--success-green)
          );
          border-radius: 4px;
          transition: width 1s ease-out;
        }

        /* CTA Section */
        .impact-cta {
          padding: 6rem 0;
          background: linear-gradient(
            135deg,
            var(--secondary-blue),
            var(--primary-blue)
          );
          color: white;
          position: relative;
        }

        .impact-cta::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="rgba(255,255,255,0.1)"/><circle cx="80" cy="80" r="1.5" fill="rgba(255,255,255,0.08)"/><circle cx="40" cy="70" r="1" fill="rgba(255,255,255,0.06)"/></svg>')
            repeat;
          background-size: 100px 100px;
        }

        .cta-title {
          font-size: clamp(2.5rem, 4vw, 3.5rem);
          font-weight: 700;
          margin-bottom: 1.5rem;
          line-height: 1.2;
          position: relative;
        }

        .cta-description {
          font-size: 1.2rem;
          margin-bottom: 3rem;
          opacity: 0.9;
          line-height: 1.6;
          position: relative;
        }

        .cta-buttons {
          display: flex;
          gap: 1rem;
          justify-content: center;
          flex-wrap: wrap;
          position: relative;
        }

        .cta-buttons .btn {
          padding: 1rem 2.5rem;
          border-radius: 50px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .cta-buttons .btn:hover {
          transform: translateY(-2px);
        }

        /* Color Utilities */
        .bg-primary {
          background-color: var(--primary-blue) !important;
        }
        .bg-success {
          background-color: var(--success-green) !important;
        }
        .bg-warning {
          background-color: var(--accent-orange) !important;
        }
        .bg-info {
          background-color: var(--info-blue) !important;
        }

        .text-primary {
          color: var(--primary-blue) !important;
        }
        .text-success {
          color: var(--success-green) !important;
        }
        .text-warning {
          color: var(--accent-orange) !important;
        }
        .text-info {
          color: var(--info-blue) !important;
        }

        /* Button Styles */
        .btn-primary {
          background-color: var(--primary-blue);
          border-color: var(--primary-blue);
        }

        .btn-primary:hover {
          background-color: var(--secondary-blue);
          border-color: var(--secondary-blue);
        }

        .btn-warning {
          background-color: var(--accent-orange);
          border-color: var(--accent-orange);
          color: white;
        }

        .btn-warning:hover {
          background-color: #d97706;
          border-color: #d97706;
          color: white;
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          body {
            padding-top: 70px;
          }

          .impact-background {
            display: none;
          }

          .hero-highlights {
            flex-direction: column;
          }

          .highlight-item {
            justify-content: center;
          }

          .tab-navigation {
            flex-direction: column;
          }

          .tab-content {
            padding: 2rem 1rem;
          }

          .timeline-container::before {
            left: 20px;
          }

          .timeline-phase {
            padding-left: 80px;
          }

          .phase-marker {
            width: 40px;
            height: 40px;
            font-size: 1rem;
          }

          .phase-content::before {
            left: -10px;
            border-right-width: 10px;
          }

          .phase-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .metric-comparison {
            flex-direction: column;
            gap: 1.5rem;
          }

          .comparison-arrow {
            transform: rotate(90deg);
          }

          .cta-buttons {
            flex-direction: column;
            align-items: center;
          }

          .cta-buttons .btn {
            width: 100%;
            max-width: 300px;
          }
        }

        @media (max-width: 576px) {
          .metric-card {
            padding: 1.5rem;
          }

          .outcome-card {
            padding: 2rem 1.5rem;
          }

          .outcome-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
          }

          .research-card {
            padding: 1.5rem;
          }

          .research-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .change-indicator {
            align-self: flex-end;
          }
        }

        /* Print Styles */
        @media print {
          .impact-background {
            display: none;
          }

          .impact-cta {
            background: var(--light-gray) !important;
            color: var(--text-gray) !important;
          }
        }

        /* Accessibility */
        .btn:focus,
        .metric-tab:focus,
        .research-card:focus {
          outline: 2px solid var(--accent-orange);
          outline-offset: 2px;
        }

        /* Smooth Scrolling */
        html {
          scroll-behavior: smooth;
        }

        /* Loading States */
        .metric-card,
        .outcome-card {
          animation-fill-mode: both;
        }

        /* Enhanced Visual Effects */
        .research-card::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(
            90deg,
            var(--success-green),
            var(--accent-teal)
          );
          border-radius: 1.5rem 1.5rem 0 0;
        }

        .outcome-card::after {
          content: "";
          position: absolute;
          bottom: -5px;
          right: -5px;
          width: 80px;
          height: 80px;
          background: linear-gradient(
            135deg,
            var(--primary-blue),
            var(--accent-teal)
          );
          border-radius: 50%;
          opacity: 0.05;
          z-index: -1;
        }

        /* Custom Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }

        ::-webkit-scrollbar-track {
          background: var(--light-gray);
        }

        ::-webkit-scrollbar-thumb {
          background: var(--primary-blue);
          border-radius: 4px;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: var(--secondary-blue);
        }
      `}</style>

      {/* Bootstrap JS */}
      <script src="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/js/bootstrap.bundle.min.js"></script>
    </div>
  );
};

export default Impact;
